import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { DEFAULT_SURVEY_FORMS_ORDER } from '../../graphql/queries';
import { useDeleteSurveyForm, useGetSurveyForms } from '../../hooks/use-survey-forms';
import { SurveyForm, SurveyFormsPagingResult } from '../../types/survey-form.types';
import { PageHeader } from '@/components/core';
import { appPath } from '@/constant/app-paths';

export const SurveyFormListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SURVEY_FORMS_ORDER,
  });
  const [deleteTarget, setDeleteTarget] = useState<SurveyForm | null>(null);

  const { data, isLoading, error } = useGetSurveyForms({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { SurveyForms?: SurveyFormsPagingResult })?.SurveyForms;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteSurveyForm, isPending: isDeleting } = useDeleteSurveyForm();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteSurveyForm(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  const columns: ColumnDef<SurveyForm>[] = [
    {
      id: 'Title',
      meta: { filter: { type: 'text' } },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('TITLE')} />,
      accessorFn: (row) => row.Title,
    },
    {
      id: 'Description',
      meta: { filter: { type: 'text' } },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESCRIPTION')} />,
      accessorFn: (row) => row.Description ?? '-',
    },
    {
      id: 'actions',
      header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(appPath.surveyForm(row.original.ItemId))}
            aria-label={t('EDIT')}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteTarget(row.original)}
            aria-label={t('DELETE')}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('SURVEY_FORMS')}
        actions={[
          {
            label: t('CREATE_SURVEY_FORM'),
            icon: <Plus className="size-5" />,
            onClick: () => navigate(appPath.surveyForm('new')),
          },
        ]}
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('DELETE_SURVEY_FORM')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
